import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../components/AdminLayout';
import { usePartnerRecords } from '../../hooks/usePartnerRecords';
import {
  partnersApi,
  PartnerRecord,
  PartnerReviewStatus,
  partnerStatusChipClass,
  partnerStatusLabel,
} from '../../api/partners';

/**
 * One partner's complete profile plus the staff FSM controls — the row link
 * target from PartnersSettings' table. The profile panel is its own
 * label/value list from `PartnerViewSet.profile`, not `@kevlarcon/formkit`'s
 * `FormRenderer`: that package's schema builder mishandles the
 * `[(raw, label), ...]` choices shape every 'partner' seed choice field uses
 * (school_type/grade_low/grade_high), so reloading an existing submission
 * through it always shows those three fields blank — see
 * partners/views.py's `_resolve_choice_label` docstring.
 *
 * FSM controls mirror `partners.views.ALLOWED_TRANSITIONS` exactly —
 * `submitted` can publish or reject, `rejected` can go back to `submitted`
 * for revision, and `imported`/`published`/`partnered` show status only,
 * since the backend defines no staff-initiated transition off any of them.
 */
const PartnerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const submissionId = Number(id);
  const queryClient = useQueryClient();
  const { formId, records, isLoading } = usePartnerRecords();
  const [reason, setReason] = useState('');

  const record = records?.find((r) => r.id === submissionId);

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['partner-profile', formId, submissionId],
    queryFn: () => partnersApi.getProfile(formId!, submissionId),
    enabled: formId != null,
  });

  const mutation = useMutation({
    mutationFn: (status: PartnerReviewStatus) =>
      partnersApi.review(formId!, submissionId, status, status === 'rejected' ? reason : undefined),
    onSuccess: (result) => {
      setReason('');
      queryClient.setQueryData<PartnerRecord[]>(['partner-records', formId], (prev) =>
        (prev ?? []).map((r) =>
          r.id === submissionId
            ? { ...r, status: result.status, rejection_reason: result.rejection_reason }
            : r,
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['partner-queue', formId] });
    },
  });

  const backLink = (
    <Link
      to="/settings/partners"
      className="sgk-btn sgk-btn--onnavy sgk-btn--sm"
      data-testid="partner-detail-back-link"
    >
      ← All partners
    </Link>
  );

  if (isLoading || !records) {
    return (
      <AdminLayout title="Partner" actions={backLink} wide>
        <p className="sgk-admin__sub" data-testid="partner-detail-loading">Loading…</p>
      </AdminLayout>
    );
  }

  if (!record || formId == null) {
    return (
      <AdminLayout title="Partner not found" actions={backLink} wide>
        <p className="sgk-admin__sub" data-testid="partner-detail-not-found">
          This partner record doesn’t exist, or belongs to a different organization.
        </p>
      </AdminLayout>
    );
  }

  const subtitle = [record.city, record.state].filter(Boolean).join(', ') || undefined;

  return (
    <AdminLayout title={record.school_name || '(untitled)'} subtitle={subtitle} actions={backLink} wide>
      <div className="sgk-panel" data-testid="partner-detail-status-panel">
        <h2 className="sgk-panel__title">Status</h2>
        <p className="sgk-panel__hint">
          <span className={partnerStatusChipClass(record.status)} data-testid="partner-detail-status-chip">
            {partnerStatusLabel(record.status)}
          </span>
          {' · '}
          {record.claimed ? 'Claimed' : 'Unclaimed'}
        </p>

        {record.status === 'rejected' && record.rejection_reason && (
          <p className="sgk-admin__sub" data-testid="partner-detail-rejection-reason">
            Rejected: {record.rejection_reason}
          </p>
        )}

        {record.status === 'submitted' && (
          <>
            <div className="sgk-field">
              <label htmlFor="partner-detail-reject-reason-input">Rejection reason (optional)</label>
              <input
                id="partner-detail-reject-reason-input"
                type="text"
                className="sgk-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="partner-detail-reject-reason-input"
              />
            </div>
            <div className="sgk-btnrow">
              <button
                type="button"
                className="sgk-btn sgk-btn--gold sgk-btn--sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate('published')}
                data-testid="partner-detail-publish-button"
              >
                Publish
              </button>
              <button
                type="button"
                className="sgk-btn sgk-btn--ghost sgk-btn--sm"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate('rejected')}
                data-testid="partner-detail-reject-button"
              >
                Reject
              </button>
            </div>
          </>
        )}

        {record.status === 'rejected' && (
          <div className="sgk-btnrow">
            <button
              type="button"
              className="sgk-btn sgk-btn--gold sgk-btn--sm"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate('submitted')}
              data-testid="partner-detail-send-back-button"
            >
              Send back for revision
            </button>
          </div>
        )}

        {record.status === 'imported' && (
          <p className="sgk-admin__sub" data-testid="partner-detail-imported-note">
            Waiting on the school to claim and submit this listing before it can be reviewed.
          </p>
        )}
      </div>

      <div className="sgk-panel">
        <h2 className="sgk-panel__title">Profile</h2>
        {profileLoading || !profile ? (
          <p className="sgk-admin__sub" data-testid="partner-detail-profile-loading">Loading…</p>
        ) : (
          <dl className="sgk-proflist" data-testid="partner-detail-profile">
            {profile.map((field) => (
              <div className="sgk-proflist__row" key={field.name}>
                <dt>{field.label}</dt>
                <dd data-testid={`partner-detail-profile-${field.name}`}>
                  {field.value || <span className="sgk-proflist__empty">—</span>}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </AdminLayout>
  );
};

export default PartnerDetail;
